import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import type { Config as DOMPurifyConfig } from 'dompurify';
import parse, { Element, domToReact, attributesToProps } from 'html-react-parser';
import type { DOMNode, HTMLReactParserOptions } from 'html-react-parser';

import Box from '@mui/material/Box';
import type { BoxProps } from '@mui/material/Box';

// ----------------------------------------------------------------------

/**
 * Renders untrusted HTML (inbound e-mail bodies, notification copy, rendered templates).
 *
 * Two independent layers, in this order:
 *
 * 1. **DOMPurify** strips anything executable. `USE_PROFILES: { html: true }` restricts the
 *    output to the HTML allow-list, which deliberately excludes `<script>`, `<iframe>`,
 *    `<object>`, SVG and MathML, and drops every `on*` event-handler attribute and unsafe
 *    URI scheme (`javascript:`, `data:` in href/src).
 * 2. **html-react-parser** turns the cleaned markup into real React elements, so nothing is
 *    ever handed to `dangerouslySetInnerHTML`. That keeps the `react/no-danger` lint rule
 *    absolute - there is no sanctioned exception anywhere in the app - and means a future
 *    DOMPurify bypass still has to survive React's own attribute handling, which never
 *    evaluates a string as an event handler.
 *
 * SVG and MathML are excluded on purpose: e-mail bodies have no legitimate need for them and
 * they are a recurring source of sanitiser bypasses (e.g. `<svg><animate onbegin=...>`).
 *
 * @see src/tests/security in the playwright-automation repo for the XSS suite that pins this
 * behaviour (XSS-01 / XSS-02).
 */

const SANITIZE_CONFIG: DOMPurifyConfig = {
  USE_PROFILES: { html: true },
  // Keep relative/absolute http(s) and mailto links, drop everything exotic.
  ALLOW_DATA_ATTR: false,
  // `srcdoc` would smuggle a whole document in through an allowed element.
  FORBID_ATTR: ['srcdoc'],
};

const parserOptions: HTMLReactParserOptions = {
  replace: (node) => {
    // Harden links: untrusted content must not be able to drive the operator's tab, and
    // anything that opens a new tab needs noopener/noreferrer.
    if (node instanceof Element && node.tagName === 'a') {
      const props = attributesToProps(node.attribs);

      return (
        <a {...props} target="_blank" rel="noopener noreferrer">
          {domToReact(node.children as DOMNode[], parserOptions)}
        </a>
      );
    }

    return undefined;
  },
};

export type SafeHtmlProps = Omit<BoxProps, 'children' | 'dangerouslySetInnerHTML'> & {
  /** Untrusted HTML. Sanitised before it is turned into React elements. */
  html?: string | null;
};

export function SafeHtml({ html, ...other }: SafeHtmlProps) {
  const content = useMemo(() => {
    if (!html) {
      return null;
    }

    return parse(DOMPurify.sanitize(html, SANITIZE_CONFIG), parserOptions);
  }, [html]);

  return <Box {...other}>{content}</Box>;
}
